<template>
  <th
    v-if="props.column.type === 'checkbox-selection' && columnHeaderVisible(props.column)"
    class="row-column column-checkbox-selection"
    :class="{ 'column-moveable': props.column.moveable }"
  >
    <hcl-form-input-checkbox
      v-if="checkboxSelectionVisible(props.column)"
      :id="props.column.name"
      :name="props.column.name"
      label=""
      value="true"
      :checked="checkboxSelectionRef.header.selected ? 'true' : 'false'"
      v-on:hcl-form-input-checkbox-changed="checkboxSelectionToggle(props.column)"
    ></hcl-form-input-checkbox>
  </th>
  <th
    v-else-if="props.column.type === 'checkbox' && columnHeaderVisible(props.column)"
    class="row-column column-checkbox"
    :class="{ 'column-moveable': props.column.moveable }"
  >
    <div class="column-entry">{{ props.column.title }}</div>
  </th>
  <th
    v-else-if="props.column.type === 'actions' && columnHeaderVisible(props.column)"
    class="row-column column-actions"
    :class="{ 'column-moveable': props.column.moveable }"
  ></th>
  <th
    v-else-if="props.column.type === 'collapsible' && columnHeaderVisible(props.column)"
    class="row-column"
    :class="{ 'column-moveable': props.column.moveable }"
  >
    <div class="column-collapsible"></div>
  </th>
  <th
    v-else-if="props.column.type === 'stack' && columnHeaderVisible(props.column)"
    class="row-column column-stack"
    :class="{ 'column-moveable': props.column.moveable }"
  >
    <div class="stack-wrapper">
      <div
        class="column-entry"
        v-for="item in props.column.items"
        :key="item.name"
      >
        <template v-if="columnHeaderVisible(item)">
          <span>{{ item.title }}</span>
          <span
            v-if="item.sortable"
            class="entry-sorter"
            @click.prevent="onColumnSortClick(item)"
          >
            <span v-if="columnHeaderSorting(item) === 'asc'">
              <icon-svg
                name="icon-sort-up"
                class="entry-sorter-icon icon-sort-up"
              ></icon-svg>
            </span>
            <span v-else-if="columnHeaderSorting(item) === 'desc'">
              <icon-svg
                name="icon-sort-down"
                class="entry-sorter-icon icon-sort-down"
              ></icon-svg>
            </span>
            <span v-else>
              <icon-svg
                name="icon-sort-none"
                class="entry-sorter-icon icon-sort-none"
              ></icon-svg>
            </span>
          </span>
        </template>
      </div>
    </div>
  </th>
  <th
    v-else-if="columnHeaderVisible(props.column)"
    class="row-column column-value column-no-wrap"
    :class="{ 'column-moveable': props.column.moveable }"
  >
    <div class="column-entry">
      {{ props.column.title }}
      <span
        v-if="props.column.sortable"
        class="entry-sorter"
        @click.prevent="onColumnSortClick(props.column)"
      >
        <span v-if="props.column.sort && props.sorting?.sortDirection === 'asc'">
          <icon-svg
            name="icon-sort-up"
            class="entry-sorter-icon icon-sort-up"
          ></icon-svg>
        </span>
        <span v-else-if="props.column.sort && props.sorting?.sortDirection === 'desc'">
          <icon-svg
            name="icon-sort-down"
            class="entry-sorter-icon icon-sort-down"
          ></icon-svg>
        </span>
        <span v-else>
          <icon-svg
            name="icon-sort-none"
            class="entry-sorter-icon icon-sort-none"
          ></icon-svg>
        </span>
      </span>
    </div>
  </th>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import { DefinitionListContentColumn, DefinitionListContentConfigurationSelection, DefinitionListHeaderSorting, DefinitionListContentConfiguration } from '@/components/types'

import IconSvg from '@/components/IconSvg/IconSvg.vue'
import HclFormInputCheckbox from '@/components/FormInputCheckbox/FormInputCheckbox.vue'

interface Props {
  column: DefinitionListContentColumn
  selection: DefinitionListContentConfigurationSelection | undefined
  sorting: DefinitionListHeaderSorting | undefined
  config?: DefinitionListContentConfiguration
}

const props = withDefaults(defineProps<Props>(), {
  column: undefined,
  selection: undefined,
  sorting: undefined,
  config: undefined
})

const checkboxSelectionRef = ref({
  enabled: props.selection?.enabled,
  header: {
    selected: props.selection?.header?.selected,
    show: props.selection?.header?.show
  }
})

watch(props, (n) => {
  if (props.column.type === 'checkbox-selection' || props.column.type === 'checkbox') {
    if (n.selection) {
      checkboxSelectionRef.value.enabled = n.selection.enabled
      checkboxSelectionRef.value.header.selected = n.selection.header.selected
      checkboxSelectionRef.value.header.show = n.selection.header.show
    }
  }
})

const emit = defineEmits(['hcl-column-header-checkbox-selection-change', 'hcl-column-header-sort-change'])

const checkboxSelectionVisible = (column: DefinitionListContentColumn) => {
  return column.name === props.config?.rows.columns.selection.column
    ? checkboxSelectionRef.value.header.show
    : true
}

const checkboxSelectionToggle = (column: DefinitionListContentColumn) => {
  if (column.name === props.config?.rows.columns.selection.column) {
    checkboxSelectionRef.value.header.selected = !checkboxSelectionRef.value.header.selected
  }

  emit('hcl-column-header-checkbox-selection-change', { column: column, checked: checkboxSelectionRef.value.header.selected })
}

const onColumnSortClick = (column: DefinitionListContentColumn) => {
  emit('hcl-column-header-sort-change', column)
}

const columnHeaderVisible = (column: DefinitionListContentColumn) => {
  if (column.type === 'stack' && column.hidden) return false

  if (column.type === 'stack' && column.items.length > 0) {
    return !column.items.every((column) => {
      return column.hidden
    })
  }

  if (column.type === 'checkbox-selection' || column.type === 'checkbox')
    return column.name === props.config?.rows.columns.selection.column
      ? checkboxSelectionRef.value.enabled
      : !column.hidden

  return !column.hidden
}

const columnHeaderSorting = (column: DefinitionListContentColumn) => {
  if (props.sorting?.items) {
    const result = props.sorting.items.filter((item) => {
      return item.name === column.name
    })
    return result.length ? result[0].sortDirection : ''
  }

  return props.sorting?.sortDirection
}
</script>
